// src/components/Clients/EditClient.js
import React, { useEffect, useState } from "react";
import { Formik, Form, FieldArray } from "formik";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useLocation } from "react-router-dom";
import * as Yup from "yup";
import {
    Button, Grid, TextField, FormControl, Select, MenuItem, FormHelperText,
    Divider, Typography, Collapse, IconButton, Box, Autocomplete
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Notification } from "../../utils/Notification";
import API from "../../utils/Axios.utils";
import { Country, State, City } from "country-state-city";

const EditClient = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const clientDetails = location.state?.clientDetails; // passed from listing page

    const [alert, setAlert] = React.useState(false);
    const [alertContent, setAlertContent] = React.useState("");
    const [alertType, setAlertType] = React.useState("");
    const [contacts, setContacts] = useState([]); // local state for contacts
    const handleAlert = () => setAlert(false);

    const [showContactSection, setShowContactSection] = useState(true);

    // Country / State / City
    const [countries] = useState(Country.getAllCountries());
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [selectedState, setSelectedState] = useState(null);

    const token = JSON.parse(localStorage.getItem("userLoggedIn")).token;
    const config = { headers: { Authorization: `Bearer ${token}` } };

    const fetchContacts = async () => {
        if (!clientDetails?.client_id) return;
        try {
            const res = await API.get(`/api/client/ClientsContacts/${clientDetails.client_id}`, config);
            const activeContacts = (res?.data?.contacts || [])
                .filter(c => c.is_deleted === 'N')
                .map(c => ({
                    contact_id: c.contact_id,
                    name: c.name || "",
                    email: c.email || "",
                    phone: c.phone || "",
                }));
            setContacts(activeContacts);
        } catch (error) {
            console.error("Failed to fetch contacts on edit:", error);
        }
    };

    useEffect(() => {
        fetchContacts();
    }, [clientDetails?.client_id]);

    // Dropdown options (same as AddClient)
    const industryOptions = [
        { label: "IT Services & Consulting", value: "IT_Services_&_Consulting" },
        { label: "Healthcare", value: "Healthcare" },
        { label: "Manufacturing", value: "Manufacturing" },
        { label: "Recruitment & staffing agency", value: "Recruitment_&_staffing_agency" },
        { label: "IT Product & services", value: "IT_Product_&_services" },
        { label: "Banking & finance", value: "Banking_&_finance" }
    ];

    const headCountOptions = [
        { label: "0-50", value: "0-50" },
        { label: "50-200", value: "50-200" },
        { label: "200-500", value: "200-500" },
        { label: "500-1000", value: "500-1000" },
        { label: "1000-5000", value: "1000-5000" },
        { label: "5000-10000", value: "5000-10000" },
        { label: "10000-100000", value: "10000-100000" }
    ];

    // Initial values from existing client
    const initialValues = {
        name: clientDetails?.name || "",
        industry: clientDetails?.industry || "",
        country: clientDetails?.country || "",
        state: clientDetails?.state || "",
        city: clientDetails?.city || "",
        headCount: clientDetails?.head_count || "",
        revenue: clientDetails?.revenue || "",
        primaryContactNumber: clientDetails?.primary_contact_number || "",
        primaryContactEmail: clientDetails?.primary_contact_email || "",
        contactPersons: contacts,
    };

    // Validation (same as AddClient)
    const validationSchema = Yup.object().shape({
        name: Yup.string()
            .trim()
            .required("Client name is required")
            .min(2, "Minimum 2 characters")
            .max(100, "Name is too long")
            .matches(/^[a-zA-Z0-9\s]+$/, "Only letters, numbers, and spaces are allowed"),
        industry: Yup.string().required("Industry is required"),
        country: Yup.string().required("Country is required"),
        state: Yup.string().required("State is required"),
        city: Yup.string().required("City is required"),
        primaryContactNumber: Yup.string().trim().required("Primary contact number is required").matches(/^[0-9]{10}$/, "Primary contact number must be exactly 10 digits"),
        primaryContactEmail: Yup.string().trim()
            .required("Primary contact email is required")
            .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, "Invalid email format"),
        headCount: Yup.string().optional(),
        revenue: Yup.number().min(0, "Revenue cannot be negative").optional(),
        contactPersons: Yup.array()
            .of(
                Yup.object().shape({
                    name: Yup.string()
                        .trim()
                        .required("Contact name is required")
                        .min(2, "Minimum 2 characters")
                        .max(100, "Name is too long")
                        .matches(/^[a-zA-Z\s]+$/, "Only letters and spaces are allowed"),
                    email: Yup.string().trim().required("Contact email is required").matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, "Invalid email format"),
                    phone: Yup.string().trim().required("Contact phone is required").matches(/^[0-9]{10}$/, "Contact phone must be exactly 10 digits"),
                })
            )
            .test("unique-contacts", "Duplicate contact persons are not allowed", (contactPersons) => {
                if (!contactPersons) return true;
                const seen = new Set();
                for (const contact of contactPersons) {
                    const identifier = `${contact.name?.trim().toLowerCase()}|${contact.email?.trim().toLowerCase()}|${contact.phone?.trim()}`;
                    if (seen.has(identifier)) return false;
                    seen.add(identifier);
                }
                return true;
            }),
    });

    // Initialize location selectors from existing client
    useEffect(() => {
        if (clientDetails?.country) {
            const c = countries.find((x) => x.name === clientDetails.country) || null;
            setSelectedCountry(c);
            if (c) {
                const sList = State.getStatesOfCountry(c.isoCode);
                setStates(sList);
            }
        }
    }, [clientDetails, countries]);

    useEffect(() => {
        if (clientDetails?.state && selectedCountry) {
            const s =
                State.getStatesOfCountry(selectedCountry.isoCode).find(
                    (x) => x.name === clientDetails.state
                ) || null;
            setSelectedState(s);
            if (s) {
                const cList = City.getCitiesOfState(s.countryCode, s.isoCode);
                setCities(cList);
            }
        }
    }, [clientDetails, selectedCountry]);

    // React to manual country/state changes (same as AddClient)
    useEffect(() => {
        if (selectedCountry && !clientDetails?.country) {
            setStates(State.getStatesOfCountry(selectedCountry.isoCode));
            setCities([]);
        }
    }, [selectedCountry, clientDetails]);

    useEffect(() => {
        if (selectedState && !clientDetails?.state) {
            setCities(City.getCitiesOfState(selectedState.countryCode, selectedState.isoCode));
        }
    }, [selectedState, clientDetails]);

    const onSubmit = async (values, { setSubmitting }) => {
        try {
            const token = JSON.parse(localStorage.getItem("userLoggedIn")).token;
            const config = { headers: { Authorization: `Bearer ${token}` } };

            const validContactPersons = values.contactPersons.filter(
                (person) =>
                    (person.name && person.name.trim() !== "") ||
                    (person.email && person.email.trim() !== "") ||
                    (person.phone && person.phone.trim() !== "")
            ).map(person => ({
                ...person,
                name: person.name.trim(),
                email: person.email.trim()
            }));

            const payload = {
                name: values.name.trim(),
                industry: values.industry,
                location: `${values.city}, ${values.state}, ${values.country}`,
                head_count: values.headCount,
                revenue: values.revenue,
                primary_contact_number: values.primaryContactNumber,
                primary_contact_email: values.primaryContactEmail.trim(),
                role: "client",
                contact_persons: validContactPersons,
            };

            const res = await API.put(
                `/api/client/${clientDetails.client_id}`,
                payload,
                config
            );

            setAlertContent(
                res?.data?.message ||
                `Client and ${validContactPersons.length} contact person(s) updated successfully!`
            );
            setAlertType("success");
            setAlert(true);
            const returnPage = location.state?.fromPage || 1;
            setTimeout(() => navigate("/clients", { state: { page: returnPage } }), 1500);
        } catch (error) {
            setAlertContent(
                error?.response?.data?.message || "Failed to update client"
            );
            setAlertType("error");
            setAlert(true);
        } finally {
            setSubmitting(false);
        }
    };

    const navigateToPreviousPage = () => {
        const returnPage = location.state?.fromPage || 1;
        navigate("/clients", { state: { page: returnPage } });
    };

    return (
        <div style={{ marginTop: "20px" }}>
            <Button startIcon={<ArrowBackIcon />} onClick={navigateToPreviousPage}>
                Back
            </Button>
            <div
                style={{
                    border: "1px solid #989898",
                    borderRadius: "10px",
                    padding: "15px",
                }}
            >
                <div
                    style={{
                        fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                        fontSize: "20px",
                        fontWeight: "500",
                        letterSpacing: "1",
                        textAlign: "left",
                    }}
                >
                    <span>Edit Client</span>
                </div>

                <Formik
                    enableReinitialize
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={onSubmit}
                >
                    {({
                        values,
                        errors,
                        touched,
                        handleBlur,
                        handleChange,
                        isSubmitting,
                        setFieldValue,
                        dirty,
                        isValid
                    }) => (
                        <Form noValidate>
                            <Grid container spacing={2}>
                                {/* CLIENT FIELDS */}
                                <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                                    <span style={{ fontSize: "16px" }}>
                                        Client Name <span style={{ color: "red" }}>*</span>
                                    </span>
                                    <TextField
                                        name="name"
                                        fullWidth
                                        value={values.name}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        size="small"
                                        error={touched.name && Boolean(errors.name)}
                                        helperText={touched.name && errors.name}
                                    />
                                </Grid>

                                <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                                    <span style={{ fontSize: "16px" }}>
                                        Industry <span style={{ color: "red" }}>*</span>
                                    </span>
                                    <FormControl
                                        fullWidth
                                        size="small"
                                        error={Boolean(touched.industry && errors.industry)}
                                    >
                                        <Select
                                            name="industry"
                                            value={values.industry}
                                            displayEmpty
                                            onChange={(e) =>
                                                setFieldValue("industry", e.target.value)
                                            }
                                            onBlur={handleBlur}
                                        >
                                            <MenuItem value="" disabled>
                                                Select Industry
                                            </MenuItem>
                                            {industryOptions.map((industry, index) => (
                                                <MenuItem key={index} value={industry.value}>
                                                    {industry.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText sx={{ color: "red" }}>
                                            {touched.industry && errors.industry}
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>

                                {/* Country */}
                                <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                                    <span style={{ fontSize: "16px" }}>
                                        Country <span style={{ color: "red" }}>*</span>
                                    </span>
                                    <Autocomplete
                                        options={countries}
                                        getOptionLabel={(option) => option.name || ""}
                                        value={countries.find((c) => c.name === values.country) || null}
                                        onChange={(_, selected) => {
                                            const countryName = selected ? selected.name : "";
                                            setFieldValue("country", countryName);
                                            setSelectedCountry(selected || null);
                                            setSelectedState(null);
                                            setFieldValue("state", "");
                                            setFieldValue("city", "");
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                size="small"
                                                placeholder="Select Country"
                                                error={Boolean(touched.country && errors.country)}
                                                helperText={touched.country && errors.country}
                                            />
                                        )}
                                        isOptionEqualToValue={(option, value) => option.isoCode === value.isoCode}
                                    />
                                </Grid>

                                {/* State */}
                                <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                                    <span style={{ fontSize: "16px" }}>
                                        State <span style={{ color: "red" }}>*</span>
                                    </span>
                                    <FormControl
                                        fullWidth
                                        size="small"
                                        error={Boolean(touched.state && errors.state)}
                                    >
                                        <Select
                                            name="state"
                                            value={values.state}
                                            displayEmpty
                                            onChange={(e) => {
                                                const stateName = e.target.value;
                                                setFieldValue("state", stateName);
                                                const sel =
                                                    states.find((s) => s.name === stateName) || null;
                                                setSelectedState(sel);
                                                setFieldValue("city", "");
                                                if (sel) {
                                                    setCities(
                                                        City.getCitiesOfState(
                                                            sel.countryCode,
                                                            sel.isoCode
                                                        )
                                                    );
                                                }
                                            }}
                                        >
                                            <MenuItem value="" disabled>
                                                Select State
                                            </MenuItem>
                                            {states.map((s) => (
                                                <MenuItem key={s.isoCode} value={s.name}>
                                                    {s.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText sx={{ color: "red" }}>
                                            {touched.state && errors.state}
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>

                                {/* City */}
                                <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                                    <span style={{ fontSize: "16px" }}>
                                        City <span style={{ color: "red" }}>*</span>
                                    </span>
                                    <FormControl
                                        fullWidth
                                        size="small"
                                        error={Boolean(touched.city && errors.city)}
                                    >
                                        <Select
                                            name="city"
                                            value={values.city}
                                            displayEmpty
                                            onChange={handleChange}
                                        >
                                            <MenuItem value="" disabled>
                                                Select City
                                            </MenuItem>
                                            {cities.map((ct) => (
                                                <MenuItem key={ct.name} value={ct.name}>
                                                    {ct.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText sx={{ color: "red" }}>
                                            {touched.city && errors.city}
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>

                                {/* Head Count */}
                                <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                                    <span style={{ fontSize: "16px" }}>Head Count</span>
                                    <FormControl fullWidth size="small">
                                        <Select
                                            name="headCount"
                                            value={values.headCount}
                                            displayEmpty
                                            onChange={handleChange}
                                        >
                                            <MenuItem value="" disabled>
                                                Select Head Count Range
                                            </MenuItem>
                                            {headCountOptions.map((range, index) => (
                                                <MenuItem key={index} value={range.value}>
                                                    {range.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Revenue */}
                                <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                                    <span style={{ fontSize: "16px" }}>Revenue</span>
                                    <TextField
                                        name="revenue"
                                        type="number"
                                        fullWidth
                                        value={values.revenue}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (val === "" || parseFloat(val) >= 0) {
                                                handleChange(e);
                                            }
                                        }}
                                        size="small"
                                        error={touched.revenue && Boolean(errors.revenue)}
                                        helperText={touched.revenue && errors.revenue}
                                    />
                                </Grid>

                                {/* Primary Email */}
                                <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                                    <span style={{ fontSize: "16px" }}>
                                        Primary Email <span style={{ color: "red" }}>*</span>
                                    </span>
                                    <TextField
                                        name="primaryContactEmail"
                                        type="email"
                                        fullWidth
                                        value={values.primaryContactEmail}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        size="small"
                                        error={
                                            touched.primaryContactEmail &&
                                            Boolean(errors.primaryContactEmail)
                                        }
                                        helperText={
                                            touched.primaryContactEmail &&
                                            errors.primaryContactEmail
                                        }
                                    />
                                </Grid>

                                {/* Primary Phone */}
                                <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                                    <span style={{ fontSize: "16px" }}>
                                        Primary Phone <span style={{ color: "red" }}>*</span>
                                    </span>
                                    <TextField
                                        name="primaryContactNumber"
                                        fullWidth
                                        value={values.primaryContactNumber}
                                        onChange={(e) => {
                                            const onlyNums = e.target.value.replace(/[^0-9]/g, "");
                                            if (onlyNums.length <= 10) {
                                                setFieldValue("primaryContactNumber", onlyNums);
                                            }
                                        }}
                                        onBlur={handleBlur}
                                        size="small"
                                        inputProps={{ maxLength: 10 }}
                                        error={
                                            touched.primaryContactNumber &&
                                            Boolean(errors.primaryContactNumber)
                                        }
                                        helperText={
                                            touched.primaryContactNumber &&
                                            errors.primaryContactNumber
                                        }
                                    />
                                </Grid>

                                {/* CONTACT PERSONS SECTION */}
                                <Grid item xl={12}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            cursor: "pointer",
                                            mb: 1,
                                        }}
                                        onClick={() => setShowContactSection(!showContactSection)}
                                    >
                                        <Typography
                                            variant="h6"
                                            sx={{ flexGrow: 1, color: "#000000ff" }}
                                        >
                                            Contact Persons *
                                        </Typography>
                                        <IconButton size="small">
                                            {showContactSection ? (
                                                <ExpandLessIcon />
                                            ) : (
                                                <ExpandMoreIcon />
                                            )}
                                        </IconButton>
                                    </Box>

                                    {typeof errors.contactPersons === "string" && (
                                        <FormHelperText sx={{ color: "red", ml: 1, mb: 1 }}>
                                            {errors.contactPersons}
                                        </FormHelperText>
                                    )}

                                    <Collapse in={showContactSection}>
                                        <Divider sx={{ mb: 2 }} />
                                        <FieldArray name="contactPersons">
                                            {({ push, remove }) => (
                                                <div>
                                                    {values.contactPersons.map((contact, index) => (
                                                        <Box key={index} sx={{ mb: 2, p: 2 }}>
                                                            <Box
                                                                sx={{
                                                                    display: "flex",
                                                                    justifyContent: "space-between",
                                                                    alignItems: "center",
                                                                    mb: 1,
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="subtitle1"
                                                                    sx={{ fontWeight: "bold", color: "#1976d2" }}
                                                                >
                                                                    Contact Person {index + 1}
                                                                </Typography>
                                                                {values.contactPersons.length > 1 && (
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => remove(index)}
                                                                        color="error"
                                                                    >
                                                                        <DeleteOutlineIcon />
                                                                    </IconButton>
                                                                )}
                                                            </Box>

                                                            <Grid container spacing={2}>
                                                                <Grid
                                                                    item
                                                                    xl={4}
                                                                    lg={4}
                                                                    md={6}
                                                                    sm={12}
                                                                    xs={12}
                                                                >
                                                                    <span style={{ fontSize: "16px" }}>
                                                                        Name <span style={{ color: "red" }}>*</span>
                                                                    </span>
                                                                    <TextField
                                                                        name={`contactPersons.${index}.name`}
                                                                        fullWidth
                                                                        value={contact.name}
                                                                        onChange={(e) => {
                                                                            const val = e.target.value;
                                                                            if (val === "" || /^[a-zA-Z\s]*$/.test(val)) {
                                                                                handleChange(e);
                                                                            }
                                                                        }}
                                                                        onBlur={handleBlur}
                                                                        size="small"
                                                                        inputProps={{ maxLength: 100 }}
                                                                        error={Boolean(
                                                                            touched.contactPersons?.[index]?.name &&
                                                                            errors.contactPersons?.[index]?.name
                                                                        )}
                                                                        helperText={
                                                                            touched.contactPersons?.[index]?.name &&
                                                                            errors.contactPersons?.[index]?.name
                                                                        }
                                                                    />
                                                                </Grid>

                                                                <Grid
                                                                    item
                                                                    xl={4}
                                                                    lg={4}
                                                                    md={6}
                                                                    sm={12}
                                                                    xs={12}
                                                                >
                                                                    <span style={{ fontSize: "16px" }}>
                                                                        Email <span style={{ color: "red" }}>*</span>
                                                                    </span>
                                                                    <TextField
                                                                        name={`contactPersons.${index}.email`}
                                                                        type="email"
                                                                        fullWidth
                                                                        value={contact.email}
                                                                        onChange={handleChange}
                                                                        onBlur={handleBlur}
                                                                        size="small"
                                                                        error={Boolean(
                                                                            touched.contactPersons?.[index]?.email &&
                                                                            errors.contactPersons?.[index]?.email
                                                                        )}
                                                                        helperText={
                                                                            touched.contactPersons?.[index]?.email &&
                                                                            errors.contactPersons?.[index]?.email
                                                                        }
                                                                    />
                                                                </Grid>

                                                                <Grid
                                                                    item
                                                                    xl={4}
                                                                    lg={4}
                                                                    md={6}
                                                                    sm={12}
                                                                    xs={12}
                                                                >
                                                                    <span style={{ fontSize: "16px" }}>
                                                                        Phone <span style={{ color: "red" }}>*</span>
                                                                    </span>
                                                                    <TextField
                                                                        name={`contactPersons.${index}.phone`}
                                                                        fullWidth
                                                                        value={contact.phone}
                                                                        onChange={(e) => {
                                                                            const onlyNums = e.target.value.replace(/[^0-9]/g, "");
                                                                            if (onlyNums.length <= 10) {
                                                                                setFieldValue(`contactPersons.${index}.phone`, onlyNums);
                                                                            }
                                                                        }}
                                                                        onBlur={handleBlur}
                                                                        size="small"
                                                                        inputProps={{ maxLength: 10 }}
                                                                        error={Boolean(
                                                                            touched.contactPersons?.[index]?.phone &&
                                                                            errors.contactPersons?.[index]?.phone
                                                                        )}
                                                                        helperText={
                                                                            touched.contactPersons?.[index]?.phone &&
                                                                            errors.contactPersons?.[index]?.phone
                                                                        }
                                                                    />
                                                                </Grid>
                                                            </Grid>
                                                        </Box>
                                                    ))}

                                                    <Button
                                                        type="button"
                                                        startIcon={<AddCircleOutlineIcon />}
                                                        onClick={() =>
                                                            push({ name: "", email: "", phone: "" })
                                                        }
                                                        sx={{ mt: 1 }}
                                                    >
                                                        Add Another Contact Person
                                                    </Button>
                                                </div>
                                            )}
                                        </FieldArray>
                                    </Collapse>
                                </Grid>

                                {/* Submit */}
                                <Grid item xl={12}>
                                    {(() => {
                                        const trimVal = (val) => (typeof val === "string" ? val.trim() : val);
                                        const hasChanged = 
                                            trimVal(values.name) !== trimVal(initialValues.name) ||
                                            trimVal(values.industry) !== trimVal(initialValues.industry) ||
                                            trimVal(values.country) !== trimVal(initialValues.country) ||
                                            trimVal(values.state) !== trimVal(initialValues.state) ||
                                            trimVal(values.city) !== trimVal(initialValues.city) ||
                                            trimVal(values.headCount) !== trimVal(initialValues.headCount) ||
                                            String(trimVal(values.revenue)) !== String(trimVal(initialValues.revenue)) ||
                                            trimVal(values.primaryContactNumber) !== trimVal(initialValues.primaryContactNumber) ||
                                            trimVal(values.primaryContactEmail) !== trimVal(initialValues.primaryContactEmail) ||
                                            (() => {
                                                const vContacts = values.contactPersons || [];
                                                const iContacts = initialValues.contactPersons || [];
                                                if (vContacts.length !== iContacts.length) return true;
                                                for (let i = 0; i < vContacts.length; i++) {
                                                    if (trimVal(vContacts[i].name) !== trimVal(iContacts[i].name)) return true;
                                                    if (trimVal(vContacts[i].email) !== trimVal(iContacts[i].email)) return true;
                                                    if (trimVal(vContacts[i].phone) !== trimVal(iContacts[i].phone)) return true;
                                                }
                                                return false;
                                            })();
                                        const isUpdateDisabled = !hasChanged || !isValid || isSubmitting;
                                        return (
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    marginTop: "20px",
                                                }}
                                            >
                                                <Button
                                                    variant="contained"
                                                    type="submit"
                                                    disabled={isUpdateDisabled}
                                                    style={{
                                                        background: isUpdateDisabled ? "#ccc" : "#2777AA",
                                                        color: isUpdateDisabled ? "#666" : "#fff",
                                                        fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                                                        fontSize: "16px",
                                                        fontWeight: "500",
                                                        letterSpacing: "1",
                                                    }}
                                                >
                                                    Update
                                                </Button>
                                            </div>
                                        );
                                    })()}
                                </Grid>
                            </Grid>

                            <Notification
                                message={alertContent}
                                open={alert}
                                severity={alertType}
                                onClose={handleAlert}
                            />
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    );
};

export default EditClient;
